import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "@/lib/api";
import { formatRands } from "@/lib/order";
import { StaffShell } from "@/components/layout/StaffShell";
import { driverNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { CenterSpinner } from "@/components/common/States";
import { cn } from "@/lib/utils";

export default function DriverEarnings() {
  const [range, setRange] = useState("week");
  const { data, isLoading } = useQuery({
    queryKey: ["driver-earnings", range],
    queryFn: () => api(`/drivers/earnings?range=${range}`),
  });

  return (
    <StaffShell items={driverNav} roleLabel="Driver" title="Earnings">
      {isLoading ? (
        <CenterSpinner />
      ) : (
        <div className="space-y-6">
          <div className="flex gap-2">
            {["day", "week"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                data-testid={`range-${r}`}
                className={cn(
                  "rounded-full border px-4 py-1.5 text-sm capitalize transition-colors",
                  range === r ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted",
                )}
              >
                {r === "day" ? "Today" : "This week"}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Earned" value={formatRands(data?.total)} />
            <Stat label="Deliveries" value={data?.deliveries ?? 0} />
            <Stat label="Lifetime" value={formatRands(data?.lifetime_earnings)} />
          </div>

          <Card className="rounded-2xl p-5">
            <h2 className="mb-4 font-display text-lg">Daily earnings</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.series || []}>
                  <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} tickLine={false} axisLine={false} fontSize={12} />
                  <Tooltip formatter={(v) => formatRands(v)} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="amount" radius={[8, 8, 0, 0]} fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </StaffShell>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="rounded-2xl p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl">{value}</p>
    </Card>
  );
}
